import { useEffect, useImperativeHandle, useState } from 'react';

import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';

import {
    IComponentProps,
    ISetSubTitleForExport,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ReportWidgetRef } from 'components/Dashboards/Reporting/reporting.types';

interface UIObject {
    [key: string]: unknown;
    state?: object | null | undefined;
}

interface Props {
    id: string;
    widgetId?: string;
    projectId?: number | null;
    contractId?: number | null;
    latestVersionId?: number;
    title: string;
    setSubTitleForExport?: ISetSubTitleForExport;
    customSubTitle?: string | null;
    ui?: UIObject;
    reportWidgetRef?: IComponentProps['reportWidgetRef'];
    onAddWidgetCallback?: ReportWidgetRef['onAddWidgetCallback'];
    apiPayload?: object;
    widgetEndpoint?: string;
}

export type UseBaseWidget = ReturnType<typeof useBaseWidget>;

export const useBaseWidget = ({
    id,
    widgetId,
    projectId,
    contractId,
    latestVersionId,
    title,
    setSubTitleForExport,
    customSubTitle = null as string | null | undefined,
    ui,
    reportWidgetRef,
    onAddWidgetCallback,
    apiPayload,
    widgetEndpoint,
}: Props) => {
    //  temp state to enable edit widget title in edit report
    const [editableTitle, setEditableTitle] = useState<string>(title);

    // Let parent access widget state and callbacks when adding or updating widget in report.
    useImperativeHandle(reportWidgetRef, () => {
        return {
            getWidgetState() {
                return ui?.state;
            },
            onAddWidgetCallback: onAddWidgetCallback,
            getWidgetTitle: () => editableTitle,
            getWidgetPayload: () => apiPayload,
            getWidgetEndpoint: () => widgetEndpoint,
        };
    }, [ui?.state, onAddWidgetCallback, editableTitle]);

    let subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });

    if (customSubTitle) {
        subTitle = customSubTitle;
    }

    // add subtitle when export pinboard to PPTX
    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle, title]);

    // link back to original/source page. id is used as hash for window scrolling to widget position in the page
    const route = `${getStringWithoutHash(window.location.href)}#${id}`;

    return {
        subTitle,
        route,
        editableTitle,
        setEditableTitle,
    };
};
